<template>
  <div class="search-page-mobile">
    <!-- 1. 顶部固定搜索导航栏 -->
    <div class="search-header-bar">
      <div class="back-btn" @click="$router.go(-1)">
        <i class="el-icon-arrow-left" />
      </div>
      <div class="search-input-wrap">
        <el-input
          v-model="keyword"
          :placeholder="searchHint"
          class="custom-search-input"
          clearable
          size="small"
          autofocus
          @focus="focus"
          @input="inputEvent"
          @keyup.enter.native="handleOnSearch"
        />
      </div>
      <div class="search-action-btn" @click="handleOnSearch">搜索</div>
    </div>

    <!-- 2. 搜索联想 / 热门搜索浮层面板 -->
    <div v-if="isFocus" class="search-suggest-panel">
      <!-- 热门搜索区块 -->
      <div v-if="hotSearchList.length > 0 && !keyword" class="suggest-section">
        <div class="section-title">
          <i class="el-icon-hot-water" /> 热门搜索
        </div>
        <div class="hot-tags-container">
          <div
            v-for="(hotSearch, index) in hotSearchList"
            :key="'hot-'+index"
            class="suggest-tag"
            @click="onClickHot(hotSearch.keyword)"
          >
            {{ hotSearch.keyword }}
          </div>
        </div>
      </div>

      <!-- 搜索建议列表区块 -->
      <div v-if="searchList.length > 0" class="suggest-section">
        <div class="suggest-list">
          <div
            v-for="(searchSuggest, index) in searchList"
            :key="'sug-'+index"
            class="suggest-list-item"
            @click="onClickHot(searchSuggest.value)"
          >
            <i class="el-icon-search" />
            <span>{{ searchSuggest.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 遮罩层 -->
    <div v-if="isFocus" class="search-mask" @click="blur" />

    <!-- 3. 搜索结果内容流 (双列网格布局) -->
    <div v-loading="loading" class="result-container">
      <div v-if="dataList.length > 0" class="video-grid">
        <div
          v-for="(video, index) in dataList"
          :key="'res-'+index"
          class="video-card-item"
        >
          <search-video-card :video="video" />
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="showEmpty && !loading" class="empty-result">
        <i class="el-icon-video-camera" />
        <p>没有找到关于 "{{ keyword }}" 的内容</p>
      </div>
    </div>

    <!-- 底部加载更多或分页提示 -->
    <div v-if="totalSize > 0" class="load-more-bar">
      <el-button
        v-if="hasNextPage"
        type="text"
        :loading="loading"
        @click="loadMore"
      >
        {{ loading ? '加载中...' : '点击加载更多' }}
      </el-button>
      <div v-else class="no-more">没有更多内容了</div>
    </div>
  </div>
</template>

<script>
import SearchVideoCard from '@/components/card/SearchVideoCard'
import { videoQuery, keywordSuggest, hotKeyword } from '@/api/vod'

export default {
  name: 'SearchIndexMobile',
  components: { SearchVideoCard },
  data() {
    return {
      currentPage: 1,
      pageSize: 12,
      totalSize: 0,
      dataList: [],
      hotSearchList: [],
      searchList: [],
      searchHint: '搜索你感兴趣的内容',
      showEmpty: false,
      loading: false,
      keyword: '',
      isFocus: false,
      timer: null
    }
  },
  computed: {
    hasNextPage() {
      return this.currentPage * this.pageSize < this.totalSize
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(newQuery) {
        this.keyword = newQuery.keyword || ''
        this.currentPage = parseInt(newQuery.pn) || 1
        if (this.keyword) {
          this.searchHandler()
          document.title = '搜索 - ' + this.keyword
        }
      }
    }
  },
  mounted() {
    this.fetchHotKeywords()
  },
  methods: {
    fetchHotKeywords() {
      hotKeyword().then(resp => {
        if (resp && resp.code === 0) this.hotSearchList = resp.data || []
      })
    },
    handleOnSearch() {
      if (!this.keyword || !this.keyword.trim()) return
      this.isFocus = false
      this.$router.push({
        path: '/search',
        query: { keyword: this.keyword.trim(), pn: 1 }
      })
    },
    searchHandler() {
      this.loading = true
      videoQuery(this.keyword, this.currentPage).then(resp => {
        if (resp.code === 0) {
          this.totalSize = resp.data.totalSize
          if (this.currentPage === 1) {
            this.dataList = resp.data.list || []
          } else {
            this.dataList = [...this.dataList, ...(resp.data.list || [])]
          }
          this.showEmpty = this.totalSize === 0
        }
      }).finally(() => {
        this.loading = false
      })
    },
    loadMore() {
      if (this.hasNextPage && !this.loading) {
        this.currentPage++
        this.searchHandler()
      }
    },
    focus() {
      this.isFocus = true
    },
    blur() {
      this.isFocus = false
    },
    inputEvent() {
      if (!this.keyword) {
        this.searchList = []
        return
      }
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        keywordSuggest(this.keyword).then(resp => {
          if (resp.code === 0) {
            this.searchList = (resp.data || []).map(item => ({ value: item.keyword }))
          }
        })
      }, 300)
    },
    onClickHot(val) {
      this.keyword = val
      this.handleOnSearch()
    }
  }
}
</script>

<style scoped lang="scss">
.search-page-mobile {
  min-height: 100vh;
  background-color: #f4f5f7;
  padding-top: 50px;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

/* 顶部固定搜索栏 */
.search-header-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 50px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  padding: 0 12px;
  z-index: 1002;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  .back-btn {
    font-size: 20px;
    color: #505050;
    margin-right: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .search-input-wrap {
    flex: 1;

    ::v-deep .el-input__inner {
      background-color: #f1f2f6;
      border: none;
      border-radius: 16px;
      height: 34px;
      line-height: 34px;
      padding-left: 15px;
      font-size: 13px;
      color: #303133;
    }
  }

  .search-action-btn {
    font-size: 14px;
    color: #fb7299;
    font-weight: 500;
    margin-left: 12px;
    cursor: pointer;
    flex-shrink: 0;
  }
}

/* 搜索建议 / 热门搜索浮层面板 */
.search-suggest-panel {
  position: fixed;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ffffff;
  z-index: 1001;
  padding: 15px;
  overflow-y: auto;

  .suggest-section {
    margin-bottom: 20px;

    .section-title {
      font-size: 13px;
      color: #909399;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      i { margin-right: 4px; color: #fb7299; }
    }

    .hot-tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .suggest-tag {
        padding: 6px 12px;
        background-color: #f6f7f9;
        border-radius: 16px;
        font-size: 12px;
        color: #303133;
      }
    }

    .suggest-list {
      .suggest-list-item {
        display: flex;
        align-items: center;
        padding: 12px 4px;
        border-bottom: 1px solid #f9f9f9;
        font-size: 14px;
        color: #303133;

        i {
          margin-right: 10px;
          color: #909399;
          font-size: 15px;
        }
      }
    }
  }
}

.search-mask {
  position: fixed;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1000;
}

/* 视频结果流布局 (双列) */
.result-container {
  padding: 8px;

  .video-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    .video-card-item {
      width: 48.5%;
      margin-bottom: 8px;
    }
  }
}

/* 底部加载 */
.load-more-bar {
  text-align: center;
  padding: 15px 0 30px;
  font-size: 13px;
  color: #909399;

  .no-more {
    font-size: 12px;
    color: #c0c4cc;
  }
}

.empty-result {
  text-align: center;
  padding-top: 100px;
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 10px;
    color: #dcdfe6;
  }
  p {
    font-size: 13px;
    margin: 0;
  }
}
</style>
